import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Play, Pause, AlertTriangle, Users, Clock, MessageCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { CommunitySession } from '@/hooks/useCommunitySessionsHook';
import EnhancedAudioPlayer from '@/components/EnhancedAudioPlayer';
import { getBackgroundMusicUrl } from '@/hooks/useBackgroundMusic';
import { LikeButton } from './LikeButton';
import { CommentsSection } from './CommentsSection';

interface CommunitySessionCardProps {
  session: CommunitySession;
  onPlay?: (sessionId: string) => void;
  isPlaying?: boolean;
}

export const CommunitySessionCard: React.FC<CommunitySessionCardProps> = ({
  session,
  onPlay,
  isPlaying = false
}) => {
  const [showPlayer, setShowPlayer] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const categoryColors: { [key: string]: string } = {
    'calm': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    'focus': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    'sleep': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
    'energy': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    'healing': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    'default': 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
  };

  const getCategoryBadgeColor = (categoryId: string) => {
    return categoryColors[categoryId] || categoryColors['default'];
  };

  const handlePlayClick = () => {
    if (onPlay) {
      onPlay(session.id);
    }
    setShowPlayer(!showPlayer);
  };

  // Format duration from seconds to minutes
  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} min`;
  };

  return (
    <Card className="group hover:shadow-lg transition-all duration-200 border-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg font-semibold line-clamp-2 group-hover:text-primary transition-colors">
            {session.title}
          </CardTitle>
          <Button
            variant={showPlayer ? "default" : "outline"}
            size="icon"
            className="shrink-0"
            onClick={handlePlayClick}
          >
            {showPlayer ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mt-2">
          {/* Category Badge */}
          <Badge variant="secondary" className={getCategoryBadgeColor(session.category_id)}>
            {session.category_id}
          </Badge>

          {/* Community Badge */}
          <Badge variant="secondary" className="gap-1">
            <Users className="h-3 w-3" />
            Community
          </Badge>

          {/* PG Warning Badge */}
          {session.content_warning && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              {session.content_warning}
            </Badge>
          )}

          {/* Featured Badge */}
          {session.is_primary && (
            <Badge variant="default" className="bg-yellow-500 text-black hover:bg-yellow-600">
              ⭐ Featured
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Session Info */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            {/* Duration */}
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {formatDuration(session.duration)}
            </div>

            {/* Submitter */}
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {session.is_anonymous || !session.submitted_by_name ?
                'Community Member' :
                session.submitted_by_name
              }
            </div>
          </div>

          {/* Approved date */}
          {session.approved_at && (
            <div className="text-xs">
              {formatDistanceToNow(new Date(session.approved_at), { addSuffix: true })}
            </div>
          )}
        </div>

        {/* Engagement Section */}
        <div className="flex items-center gap-3 pt-2">
          <LikeButton sessionId={session.id} />
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 h-8 px-2"
            onClick={() => setShowComments(!showComments)}
          >
            <MessageCircle className="h-4 w-4" />
            <span className="text-sm">{session.comment_count}</span>
          </Button>
        </div>

        {/* Session Description Preview */}
        {session.content && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {session.content.substring(0, 150)}
            {session.content.length > 150 && '...'}
          </p>
        )}

        {/* Audio Player (shown when playing) */}
        {showPlayer && session.audio_url && (
          <div className="pt-3 border-t border-border/50">
            <EnhancedAudioPlayer
              audioUrl={session.audio_url}
              sessionTitle={session.title}
              sessionId={session.id}
              lazyLoad={false}
              isCommunitySession={true}
              backgroundAudioUrl={
                session.background_audio_url ||
                (session.background_music_file
                  ? getBackgroundMusicUrl(session.background_music_file)
                  : undefined)
              }
              initialBackgroundVolumeDb={session.background_music_volume_db ?? undefined}
            />
          </div>
        )}

        {/* Tags */}
        {session.tags && session.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {session.tags.slice(0, 5).map((tag, index) => (
              <Badge
                key={index}
                variant="outline"
                className="text-xs"
              >
                {tag}
              </Badge>
            ))}
            {session.tags.length > 5 && (
              <Badge variant="outline" className="text-xs">
                +{session.tags.length - 5} more
              </Badge>
            )}
          </div>
        )}

        {/* Comments Section (shown when toggled) */}
        {showComments && (
          <div className="pt-4 border-t border-border/50">
            <CommentsSection sessionId={session.id} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
